import { ApiError } from './api';
import { clientEnv } from './env';

const INSTALLATION_KEY = 'hmonglus:web:installation-id';

export type GetToken = () => Promise<string | null>;

export function getInstallationId(): string {
  const stored = window.localStorage.getItem(INSTALLATION_KEY);
  if (stored) return stored;
  const id = crypto.randomUUID();
  window.localStorage.setItem(INSTALLATION_KEY, id);
  return id;
}

type LearnerApiInit = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
  auth?: boolean;
};

export async function learnerApiFetch(
  getToken: GetToken | null,
  path: string,
  init: LearnerApiInit = {},
): Promise<Response> {
  const { auth = false, headers, ...requestInit } = init;
  const token = getToken ? await getToken() : null;
  if (auth && !token) throw new ApiError('Sign in required', 401);
  const isJsonBody = typeof requestInit.body === 'string';
  return fetch(path, {
    ...requestInit,
    headers: {
      Accept: 'application/json',
      'X-Installation-Id': getInstallationId(),
      'X-Client-Platform': 'web',
      ...(clientEnv.appRegistrationKey ? { 'X-App-Key': clientEnv.appRegistrationKey } : {}),
      ...(isJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  });
}

export async function learnerApiJson<T>(
  getToken: GetToken | null,
  path: string,
  init: LearnerApiInit = {},
): Promise<T> {
  const response = await learnerApiFetch(getToken, path, init);
  if (!response.ok) {
    const bodyText = await response.text().catch(() => '');
    let detail = bodyText;
    try {
      const body = bodyText ? JSON.parse(bodyText) : null;
      detail = typeof body?.detail === 'string' ? body.detail : bodyText;
    } catch {
      // Keep raw response text.
    }
    throw new ApiError(`Request failed: ${response.status}`, response.status, detail, bodyText);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}
